<script setup lang="ts">
import { escapeRegExp } from 'lodash-es';
import { computed, onMounted, useTemplateRef } from 'vue';

import { useGlobalStyle } from '@/store/templateData';
import { isUrl } from '@/utils/validators/common';

defineOptions({
  name: 'ModuleTextDisplay'
});

interface Props {
  module?: {
    components?: {
      title?: {
        text?: string;
      };
      subtitle?: {
        text?: string;
      };
      description?: {
        text?: string;
      };
      replaceHyperLink?: {
        text?: string;
        url?: string;
      };
    };
  };
}

const props = withDefaults(defineProps<Props>(), {
  module: () => ({
    components: {
      title: {
        text: ''
      },
      subtitle: {
        text: ''
      },
      description: {
        text: ''
      },
      replaceHyperLink: {
        text: '',
        url: ''
      }
    }
  })
});

const components = computed(() => props.module.components);
const needRender = computed(() => [
  components.value?.title?.text?.trim(),
  components.value?.subtitle?.text?.trim(),
  components.value?.description?.text?.trim(),
].some(Boolean));

// #region 替換成文字連結
const descriptionRef = useTemplateRef('descriptionRef');

// NOTE:
// 避免使用 v-html 直接渲染不可信的 HTML 內容到 DOM 中
// 由於 v-html 會繞過 Vue 的模板系統直接插入 HTML，
// 若內容來自使用者輸入或不可信來源，可能導致 XSS 攻擊，
// 此處我們使用替代方案來安全地處理/顯示 HTML 內容。
const triggerReplaceHyperLink = () => {
  // 取得用戶輸入
  const fullText = components.value?.description?.text;
  const linkText = components.value?.replaceHyperLink?.text;
  const linkUrl = components.value?.replaceHyperLink?.url;
  const outputDiv = descriptionRef.value;

  if (!fullText) {
    return;
  }

  if (!outputDiv) {
    return;
  }

  if (!linkText || !(linkUrl && isUrl(linkUrl))) {
    outputDiv.textContent = fullText;

    return;
  }

  if (outputDiv) {
    outputDiv.innerHTML = '';
  }

  // 創建一個正則表達式來匹配所有出現的連結文字
  // 使用 RegExp 對象以便轉義特殊字符
  const escapedLinkText = escapeRegExp(linkText);
  const regex = new RegExp(`(${escapedLinkText})`, 'g');

  // 將文本分割成段落（連結文字前後的文本）
  const segments = fullText.split(regex);

  // 遍歷所有段落
  segments.forEach((segment) => {
    // 如果當前段落等於連結文字，則創建一個連結元素
    if (segment === linkText) {
      const linkElement = document.createElement('a');
      linkElement.href = linkUrl;
      linkElement.target = '_blank';
      linkElement.rel = 'noopener noreferrer';
      linkElement.textContent = linkText;
      linkElement.style.cursor = 'pointer';
      if (outputDiv) {
        outputDiv.appendChild(linkElement);
      }
    // 否則，創建一個文本節點
    } else if (segment) {
      const textNode = document.createTextNode(segment);
      if (outputDiv) {
        outputDiv.appendChild(textNode);
      }
    }
  });
};

// 監聽相關資料變化時，自動更新超連結
onMounted(() => {
  const linkText = components.value?.replaceHyperLink?.text ?? '';
  const linkUrl = components.value?.replaceHyperLink?.url ?? '';

  if (needRender.value && linkText && linkUrl && isUrl(linkUrl)) {
    // 觸發替換超連結
    triggerReplaceHyperLink();
  }
});
// #endregion

const { titleStyle, subtitleStyle, descriptionStyle } = useGlobalStyle();
</script>

<template>
<div class="module-text-display" v-if="needRender">
  <div
    class="module-text-display__title"
    v-if="components?.title?.text"
    :style="titleStyle"
  >
    {{ components.title.text }}
  </div>
  <div
    class="module-text-display__subtitle"
    v-if="components?.subtitle?.text"
    :style="subtitleStyle"
  >
    {{ components.subtitle.text }}
  </div>
  <div
    class="module-text-display__desc"
    ref="descriptionRef"
    v-if="components?.description?.text"
    :style="descriptionStyle"
  >
    {{ components.description.text }}
  </div>
</div>
</template>
<style lang="scss" scoped>
$desktop-fixed-width: 1075px;

.module-text-display {
  @apply px-5 py-4;

  &__title,
  &__subtitle {
    @apply font-medium;
  }

  &__title,
  &__subtitle,
  &__desc {
    @apply mb-4 ;
    @apply break-all;

    word-wrap: break-word;
  }

  &__title {
    @apply text-4xl text-center whitespace-pre-line;
  }

  &__subtitle {
    @apply text-xl text-left whitespace-pre-line;
  }

  &__desc {
    @apply text-base text-left whitespace-pre-line;
  }
}

// 平板
.tablet .module-text-display {
  @apply px-10;
}

// PC - 1024px
.laptop .module-text-display {
  @apply py-8;

  padding-left: 10%;
  padding-right: 10%;
}

// PC - 1440px
.desktop .module-text-display {
  @apply py-8;

  width: $desktop-fixed-width;
}
</style>
